import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import viinexService from '@/services/viinex';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { formatDateWithSeconds } from '@/utils/date';
import { isRecordingUnavailable } from '@/utils/camera-stream';
import type { StreamingTimeline } from '@/types/streaming';
import { VideoProgressBarTimestamp } from './VideoProgressBarTimestamp';
import {
    clampTooltipPosition,
    roundTimestamp,
    PROGRESS_THUMBNAIL_HEIGHT,
    PROGRESS_THUMBNAIL_WIDTH,
    PROGRESS_THUMBNAIL_PADDING,
} from '@/utils/video-player';

const SNAPSHOT_ROUNDING_SECONDS = 1;
const SNAPSHOT_DEBOUNCE_MS = 250;

interface CameraProgressBarThumbnailProps {
    hoveredTime: number;
    mouseX: number;
    progressBarWidth: number;
    earliestTimestamp: number;
    storageService: string;
    sourceId: string;
    timeline: StreamingTimeline | null;
}

export function CameraProgressBarThumbnail({
    hoveredTime,
    mouseX,
    progressBarWidth,
    earliestTimestamp,
    storageService,
    sourceId,
    timeline,
}: CameraProgressBarThumbnailProps) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const absoluteTimestamp = earliestTimestamp + hoveredTime;
    const roundedTimestamp = roundTimestamp(
        absoluteTimestamp,
        SNAPSHOT_ROUNDING_SECONDS,
    );
    const snapshotTimestampMs = Math.floor(
        roundedTimestamp * MILLISECONDS_TO_SECONDS,
    );

    const snapshotUrl = viinexService.getCameraSnapshotUrl(sourceId, {
        height: PROGRESS_THUMBNAIL_HEIGHT,
        storageService,
        timestamp: snapshotTimestampMs,
    });
    const currentTimestamp = Date.now() / MILLISECONDS_TO_SECONDS;
    const unavailable =
        isRecordingUnavailable(absoluteTimestamp, timeline, currentTimestamp) ||
        isRecordingUnavailable(roundedTimestamp, timeline, currentTimestamp);
    const debouncedSnapshotUrl = useDebounce<string | null>(
        unavailable ? null : snapshotUrl,
        SNAPSHOT_DEBOUNCE_MS,
        null,
    );
    const snapshotReady = debouncedSnapshotUrl === snapshotUrl;

    useEffect(() => {
        setIsLoaded(false);
        setHasError(false);
    }, [snapshotUrl]);

    const formattedTime = formatDateWithSeconds(absoluteTimestamp);

    const leftPosition = clampTooltipPosition(
        mouseX,
        PROGRESS_THUMBNAIL_WIDTH,
        progressBarWidth,
        PROGRESS_THUMBNAIL_PADDING,
    );

    const handleLoad = useCallback(() => {
        setIsLoaded(true);
        setHasError(false);
    }, []);

    const handleError = useCallback(() => {
        setIsLoaded(false);
        setHasError(true);
    }, []);

    if (unavailable) {
        return (
            <VideoProgressBarTimestamp
                hoveredTime={hoveredTime}
                mouseX={mouseX}
                progressBarWidth={progressBarWidth}
                earliestTimestamp={earliestTimestamp}
            />
        );
    }

    return (
        <div
            className="absolute bottom-full mb-[-8px] pointer-events-none"
            style={{ left: `${leftPosition}px` }}
        >
            <div className="bg-grey-800 rounded-lg overflow-hidden shadow-lg">
                <div
                    className="relative bg-black flex items-center justify-center"
                    style={{
                        height: `${PROGRESS_THUMBNAIL_HEIGHT}px`,
                        width: `${PROGRESS_THUMBNAIL_WIDTH}px`,
                    }}
                >
                    {!isLoaded && !hasError && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </div>
                    )}
                    {hasError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-grey-550">
                            <SecurityCameraIconMirrored
                                weight="fill"
                                className="size-6 text-grey-400"
                            />
                        </div>
                    )}
                    {snapshotReady && (
                        <img
                            key={debouncedSnapshotUrl}
                            src={snapshotUrl}
                            className={`w-full h-full object-cover ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                            onLoad={handleLoad}
                            onError={handleError}
                        />
                    )}
                </div>
            </div>
            <span className="block text-center text-white-100 text-xs font-geist-mono mt-1 [text-shadow:0_0_1px_rgba(0,0,0,0.2)]">
                {formattedTime}
            </span>
        </div>
    );
}
